<template>
  <div v-if="funnelTemplates.length > 1" class="funnel-step-selector">
    <label class="funnel-step-selector__label">
      {{ t('automatedScenarios.filters.clickLink_funnelStepLabel') }}
    </label>
    <div class="funnel-step-selector__hint">
      {{ t('automatedScenarios.filters.clickLink_funnelStepHint') }}
    </div>
    <div
      v-for="tmpl in funnelTemplates"
      :key="tmpl.id"
      class="funnel-step-selector__item"
      :class="{ 'funnel-step-selector__item--selected': isSelected(tmpl.id) }"
    >
      <Checkbox
        :model-value="isSelected(tmpl.id)"
        :binary="true"
        @update:model-value="toggleTemplate(tmpl.id, $event)"
      />
      <div class="funnel-step-selector__item-preview" @click="toggleTemplate(tmpl.id)">
        <div
          class="funnel-step-selector__item-thumb"
          :style="tmpl.imageUrl ? { backgroundImage: `url(${tmpl.imageUrl})` } : {}"
        >
          <div v-if="!tmpl.imageUrl" class="funnel-step-selector__item-thumb-placeholder">
            <i class="fa-light fa-file-lines" />
          </div>
        </div>
        <span class="funnel-step-selector__item-label">{{ tmpl.name }}</span>
      </div>
      <button
        :title="t('automatedScenarios.actions.sending_channel.template_preview.htmlPreviewTooltip')"
        class="funnel-step-selector__preview-btn"
        @click.stop="openHtmlPreview(tmpl.id)"
      >
        <i class="fa-light fa-eye" />
      </button>
    </div>

    <!-- HTML Preview Slider -->
    <Teleport to="body">
      <TemplateHtmlPreviewSlider
        v-if="htmlPreviewData && htmlPreviewData.previews.length > 0"
        :previews="htmlPreviewData.previews"
        @close="htmlPreviewData = null"
      />
    </Teleport>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, ref, watch, computed, inject, ComputedRef } from 'vue';
import { useI18n } from 'vue-i18n';
import Checkbox from 'primevue/checkbox';
import { UserState } from '@/composables/User';
import { getTemplateAndRedirect } from '@/composables/flows/useSendingChannelForm';
import { parseOperatorParameters } from '@/helpers/flowParameters';
import { NestApiType, nestCall } from '@/composables/nestApi';
import { store } from '@/store';
import type { FlowStructureDto } from '@/types/flow-structure';
import TemplateHtmlPreviewSlider from '@/components/import/TemplateHtmlPreviewSlider.vue';

interface FunnelTemplate {
  id: number;
  name: string;
  imageUrl: string;
}

const getFirstImageKey = (imageKey: string | undefined | null): string => {
  if (!imageKey) return '';
  try {
    const parsed = JSON.parse(imageKey);
    if (Array.isArray(parsed) && parsed.length > 0) return parsed[0];
  } catch { /* not JSON */ }
  return imageKey;
};

export default defineComponent({
  name: 'ClickLinkFunnelStepSelector',

  components: {
    Checkbox,
    TemplateHtmlPreviewSlider,
  },

  props: {
    modelValue: {
      type: Array as PropType<number[]>,
      default: () => [],
    },
    linkedBox: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    operatorId: {
      type: String,
      default: '',
    },
  },

  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const flow = inject<ComputedRef<FlowStructureDto | null>>('flow', computed(() => null));
    const funnelTemplates = ref<FunnelTemplate[]>([]);
    const loading = ref(false);
    const htmlPreviewData = ref<{ previews: { html: string; label: string }[] } | null>(null);

    const idShop = UserState.activeShop?.id ?? 0;
    const baseImagePreviewUrl = `${process.env.VUE_APP_URL_MEDIAL_IMAGE_PREFIX_WITH_RESIZE}`;

    const getImageUrl = (imageKey: string | undefined | null): string => {
      const key = getFirstImageKey(imageKey);
      if (!key) return '';
      return `${baseImagePreviewUrl}${idShop}_${key}-isdisplay.png&w=200&${Date.now()}`;
    };

    const loadFunnelTemplates = async () => {
      if (!props.linkedBox.length || !flow.value) {
        funnelTemplates.value = [];
        return;
      }

      if (!idShop) return;

      loading.value = true;
      const templates: FunnelTemplate[] = [];
      const seenIds = new Set<number>();

      try {
        for (const stepId of props.linkedBox) {
          const step = flow.value.steps.find((s) => s.id === stepId);
          if (!step) continue;

          const params = parseOperatorParameters(step.parameters);
          const rootTemplateId = Number(params.template_display_selector_id);
          if (!rootTemplateId) continue;

          const chain = await getTemplateAndRedirect(idShop, rootTemplateId);
          if (!chain) continue;

          for (const tmpl of chain) {
            const id = Number(tmpl.id_template);
            if (seenIds.has(id)) continue;
            seenIds.add(id);

            let templateName = '';
            try {
              const config = typeof tmpl.template_config === 'string'
                ? JSON.parse(tmpl.template_config)
                : tmpl.template_config;
              templateName = config?.informations?.name || tmpl.label || `Template #${id}`;
            } catch {
              templateName = tmpl.label || `Template #${id}`;
            }

            templates.push({
              id,
              name: templateName,
              imageUrl: getImageUrl(tmpl.imageKey),
            });
          }
        }
      } catch {
        // API error — show no selector
      }

      funnelTemplates.value = templates;
      loading.value = false;

      // Cleanup : retirer de modelValue les IDs qui ne correspondent plus à un
      // template affiché (template supprimé ou retiré de la chaîne).
      if (props.modelValue.length > 0) {
        const loadedIds = new Set(templates.map((t) => t.id));
        const cleaned = props.modelValue.filter((id) => loadedIds.has(id));
        if (cleaned.length !== props.modelValue.length) {
          emit('update:modelValue', cleaned);
        }
      }
    };

    const isSelected = (id: number): boolean => props.modelValue.includes(id);

    const toggleTemplate = (id: number, checked?: boolean) => {
      const isCurrentlySelected = props.modelValue.includes(id);
      const shouldSelect = checked !== undefined ? checked : !isCurrentlySelected;
      let newValue: number[];

      if (shouldSelect && !isCurrentlySelected) {
        newValue = [...props.modelValue, id];
      } else if (!shouldSelect && isCurrentlySelected) {
        newValue = props.modelValue.filter((v) => v !== id);
      } else {
        return;
      }

      emit('update:modelValue', newValue);
    };

    const openHtmlPreview = async (templateId: number) => {
      htmlPreviewData.value = null;
      store.commit('general/showTheSpinner');
      try {
        const idTemplateB64 = btoa(String(templateId));
        const result = await nestCall(
          'post',
          NestApiType.DEFAULT,
          '/template/html-preview-selector',
          {},
          '',
          { idShop, idTemplate: idTemplateB64 },
        );

        if (result && result.success && result.previews) {
          htmlPreviewData.value = { previews: result.previews };
        }
      } catch {
        // Preview failed silently
      }
      store.commit('general/hideTheSpinner');
    };

    watch(() => props.linkedBox, () => {
      emit('update:modelValue', []);
      loadFunnelTemplates();
    }, { deep: true });

    // Si le flow est hydraté après le mount (ex: ouverture d'une boîte existante
    // avant que le store ait peuplé flow), on retry sans reset de modelValue.
    watch(flow, (value, oldValue) => {
      if (value && !oldValue) loadFunnelTemplates();
    });

    // Initial load
    loadFunnelTemplates();

    return {
      t,
      funnelTemplates,
      isSelected,
      toggleTemplate,
      openHtmlPreview,
      htmlPreviewData,
    };
  },
});
</script>

<style lang="scss" scoped>
.funnel-step-selector {
  &__label {
    display: block;
    font-weight: 600;
    margin-bottom: 0.25rem;
  }

  &__hint {
    font-size: 0.8rem;
    color: #888;
    margin-bottom: 0.5rem;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    margin-bottom: 0.4rem;
    transition: border-color 0.15s ease, background 0.15s ease;

    &:hover {
      border-color: #cbd5e1;
      background: #f8fafc;
    }

    &--selected {
      border-color: #94c748;
      background: #f7faf0;
    }
  }

  &__item-preview {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex: 1;
    min-width: 0;
    cursor: pointer;
    user-select: none;
  }

  &__item-thumb {
    width: 48px;
    height: 36px;
    border-radius: 4px;
    background-color: #f1f5f9;
    background-size: cover;
    background-position: center top;
    background-repeat: no-repeat;
    flex-shrink: 0;
    border: 1px solid #e2e8f0;

    &-placeholder {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #94a3b8;
      font-size: 1rem;
    }
  }

  &__item-label {
    font-size: 0.8125rem;
    font-weight: 500;
    color: #334155;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__preview-btn {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #e2e8f0;
    background: #ffffff;
    border-radius: 6px;
    cursor: pointer;
    color: #64748b;
    font-size: 0.8125rem;
    flex-shrink: 0;
    transition: all 0.15s ease;

    &:hover {
      color: #94c748;
      border-color: #94c748;
      background: #f7faf0;
    }
  }
}
</style>
